import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { VerifiedBadge } from './VerifiedBadge';

// Product interface matching MarketplaceProduct from MarketplacePage
interface Product {
  id: string;
  name: string;
  type: 'offer' | 'request';
  quantity: number;
  pricePerUnit: number;
  total: number;
  category?: string;
  commodityType?: string;
  origin?: string;
  regions?: string[];
  verified?: boolean;
  image?: string;
}

interface QuickViewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

/**
 * QuickViewPanel component - Right-side drawer for product quick view
 * 
 * Features:
 * - Desktop (≥1024px): Inline panel positioned next to table (400px width), height matches table
 *   - No blocking overlay, allows clicking different products without closing
 *   - Positioned in flex layout alongside table container
 * - Mobile/Tablet: Full-screen modal with backdrop overlay
 * - Product summary with verified badge, image, general info
 * - Action buttons: Buy Now, Expand View
 * - Escape key closes panel
 * - Focus trap for accessibility
 * - Smooth animations (300ms)
 * - Responsive: desktop inline panel, mobile modal
 * 
 * Layout Notes:
 * - Desktop: Renders inline in flex container, not via portal (part of document flow)
 * - Mobile: Renders via portal with backdrop overlay (modal behavior)
 * - Panel height automatically matches table container height on desktop using flexbox
 */
export const QuickViewPanel = ({ isOpen, onClose, product }: QuickViewPanelProps) => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Check window width for responsive behavior
  useEffect(() => {
    const updateWidth = () => {
      setWindowWidth(window.innerWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const isDesktop = windowWidth >= 1024;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Handle Escape key to close panel
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap: keep focus within panel when open
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        // Shift+Tab: if on first element, move to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, move to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    // Focus first element when panel opens
    firstFocusableRef.current?.focus();

    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Prevent body scroll when panel is open (only for mobile/tablet)
  useEffect(() => {
    if (isOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isDesktop]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle Buy Now navigation
  const handleBuyNow = () => {
    if (!product) return;
    navigate(`/buy/${product.id}/step-1`);
    onClose();
  };

  // Handle Expand View navigation
  const handleExpandView = () => {
    if (!product) return;
    navigate(`/marketplace/products/${product.id}`);
    onClose();
  };

  if (!isOpen || !product) return null;

  // For desktop: render as side panel aligned with table
  if (isDesktop) {
    const desktopPanel = (
      <div
        ref={panelRef}
        className="w-[400px] bg-background border border-border rounded-lg shadow-lg flex flex-col transform transition-all duration-300 ease-in-out self-stretch"
        role="dialog"
        aria-modal="false"
        aria-labelledby="quick-view-title"
      >
        {/* Header with Title */}
        <div className="flex items-center justify-between p-lg border-b border-border">
          <h2
            id="quick-view-title"
            className="text-h3 font-semibold text-text-primary"
          >
            Buy a product
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="flex-shrink-0 p-2 text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-md transition-colors"
            aria-label="Close quick view panel"
            type="button"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto p-lg">
          {/* Product Name, Image, and Verification Badge at Top */}
          <div className="mb-lg">
            <div className="flex items-start gap-md mb-md">
              <h3 className="text-h3 font-semibold text-text-primary flex-1">
                {product.name}
              </h3>
              {product.verified && (
                <div className="flex-shrink-0">
                  <VerifiedBadge />
                </div>
              )}
            </div>
            
            {/* Product Image */}
            {product.image ? (
              <div className="mb-lg">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-md"
                />
              </div>
            ) : (
              <div className="mb-lg w-full h-48 bg-background-secondary rounded-md flex items-center justify-center">
                <span className="text-text-tertiary text-small">No image available</span>
              </div>
            )}
          </div>

          {/* Payment Terms Section */}
          <div className="space-y-md">
            <h3 className="text-h4 font-semibold text-text-primary">Payment terms</h3>
            <div className="space-y-sm">
              {product.origin && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Place of origin:</span>
                  <span className="ml-2 text-base text-text-primary">{product.origin}</span>
                </div>
              )}
              {product.regions && product.regions.length > 0 && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Region(s):</span>
                  <span className="ml-2 text-base text-text-primary">
                    {product.regions.join(', ')}
                  </span>
                </div>
              )}
              {product.commodityType && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Commodity:</span>
                  <span className="ml-2 text-base text-text-primary">{product.commodityType}</span>
                </div>
              )}
              {product.category && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Category:</span>
                  <span className="ml-2 text-base text-text-primary">{product.category}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="p-lg border-t border-border space-y-md">
          <button
            onClick={handleBuyNow}
            className="w-full px-6 py-3 bg-primary text-white font-medium rounded-md hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Buy now - navigate to buy flow"
            type="button"
          >
            Buy now
          </button>
          <button
            ref={lastFocusableRef}
            onClick={handleExpandView}
            className="w-full px-6 py-3 border border-primary text-primary font-medium rounded-md hover:bg-primary-light transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Expand view - navigate to product detail page"
            type="button"
          >
            Expand view
          </button>
        </div>
      </div>
    );
    
    // Render desktop panel inline (not via portal) so it's part of the flex layout
    return desktopPanel;
  }

  // For mobile/tablet: keep full-screen modal behavior
  const panelContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-view-title"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`
          relative bg-background
          shadow-lg
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen 
            ? 'translate-x-0 translate-y-0' 
            : 'translate-y-full md:translate-y-full'
          }
        `}
        style={{
          width: '100%',
          maxHeight: isTablet ? '50vh' : '100%',
          marginTop: isTablet ? 'auto' : '0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Title */}
        <div className="flex items-center justify-between p-lg border-b border-border">
          <h2
            id="quick-view-title"
            className="text-h3 font-semibold text-text-primary"
          >
            Buy a product
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="flex-shrink-0 p-2 text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-md transition-colors"
            aria-label="Close quick view panel"
            type="button"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto p-lg">
          {/* Product Name, Image, and Verification Badge at Top */}
          <div className="mb-lg">
            <div className="flex items-start gap-md mb-md">
              <h3 className="text-h3 font-semibold text-text-primary flex-1">
                {product.name}
              </h3>
              {product.verified && (
                <div className="flex-shrink-0">
                  <VerifiedBadge />
                </div>
              )}
            </div>
            
            {/* Product Image */}
            {product.image ? (
              <div className="mb-lg">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-md"
                />
              </div>
            ) : (
              <div className="mb-lg w-full h-48 bg-background-secondary rounded-md flex items-center justify-center">
                <span className="text-text-tertiary text-small">No image available</span>
              </div>
            )}
          </div>

          {/* Payment Terms Section */}
          <div className="space-y-md">
            <h3 className="text-h4 font-semibold text-text-primary">Payment terms</h3>
            <div className="space-y-sm">
              {product.origin && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Place of origin:</span>
                  <span className="ml-2 text-base text-text-primary">{product.origin}</span>
                </div>
              )}
              {product.regions && product.regions.length > 0 && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Region(s):</span>
                  <span className="ml-2 text-base text-text-primary">
                    {product.regions.join(', ')}
                  </span>
                </div>
              )}
              {product.commodityType && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Commodity:</span>
                  <span className="ml-2 text-base text-text-primary">{product.commodityType}</span>
                </div>
              )}
              {product.category && (
                <div>
                  <span className="text-small font-medium text-text-secondary">Category:</span>
                  <span className="ml-2 text-base text-text-primary">{product.category}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="p-lg border-t border-border space-y-md">
          <button
            onClick={handleBuyNow}
            className="w-full px-6 py-3 bg-primary text-white font-medium rounded-md hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Buy now - navigate to buy flow"
            type="button"
          >
            Buy now
          </button>
          <button
            ref={lastFocusableRef}
            onClick={handleExpandView}
            className="w-full px-6 py-3 border border-primary text-primary font-medium rounded-md hover:bg-primary-light transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Expand view - navigate to product detail page"
            type="button"
          >
            Expand view
          </button>
        </div>
      </div>
    </div>
  );

  // Render panel using React Portal to avoid z-index issues
  return createPortal(panelContent, document.body);
};

